import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Edit2, CheckCircle, Plus, Minus, Trash2 } from "lucide-react";

const EditingInvoiceModal = ({
  editingInvoice,
  onClose,
  DEPARTMENT_CONFIG,
  editingDepartment,
  setEditingDepartment,
  catalog,
  editingItems,
  customItem,
  setCustomItem,
  currentUser,
  formatPrice,
  onAddProduct,
  onUpdateQty,
  onRemoveItem,
  onSave,
}) => {
  return (
    <Dialog open={!!editingInvoice} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="bg-slate-800 border-slate-700 text-white max-w-4xl max-h-[85vh] flex flex-col"
        data-testid="editing-invoice-modal"
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-blue-400 flex items-center gap-2">
            <Edit2 className="w-5 h-5" />
            Modifier {editingInvoice?.invoice_number}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            {/* LEFT SIDE: Product Catalog to Add */}
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Ajouter des produits</h3>

              {/* Department Tabs */}
              <div className="flex flex-wrap gap-1">
                {Object.entries(DEPARTMENT_CONFIG).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => setEditingDepartment(key)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-all flex items-center gap-1 ${
                        editingDepartment === key
                          ? `${config.bgColor} ${config.color} border ${config.borderColor}`
                          : "bg-slate-700/50 text-slate-400 hover:text-white"
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      <span className="inline text-[11px] sm:text-sm">{config.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Products Grid */}
              <div className="grid grid-cols-2 gap-1.5 max-h-[150px] sm:max-h-[200px] overflow-y-auto pr-1">
                {(catalog[editingDepartment] || []).map((product, idx) => (
                  <button
                    key={idx}
                    onClick={() => onAddProduct(product, editingDepartment)}
                    className="bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600 rounded-lg p-1.5 text-left transition-all"
                  >
                    <p className="text-white text-xs font-medium truncate">{product.name}</p>
                    <p className="text-amber-400 text-xs">{formatPrice(product.price)} F</p>
                  </button>
                ))}
                {(catalog[editingDepartment] || []).length === 0 && (
                  <p className="col-span-2 text-slate-500 text-xs text-center py-2">Aucun produit</p>
                )}
              </div>

              {/* Custom item in edit mode - Manager/Admin only */}
              {editingDepartment === "autres" && (currentUser?.role === "manager" || currentUser?.role === "admin") && (
                <div className="bg-slate-700/30 rounded-lg p-2 space-y-1">
                  <p className="text-xs text-slate-400 font-medium">Article personnalisé</p>
                  <div className="flex gap-1">
                    <Input
                      placeholder="Nom"
                      value={customItem.name}
                      onChange={(e) => setCustomItem({ ...customItem, name: e.target.value })}
                      className="bg-slate-700/50 border-slate-600 text-white text-xs h-8 flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Prix"
                      value={customItem.price || ""}
                      onChange={(e) => setCustomItem({ ...customItem, price: parseInt(e.target.value) || 0 })}
                      className="bg-slate-700/50 border-slate-600 text-white text-xs h-8 w-16"
                    />
                    <Button
                      size="sm"
                      onClick={() => {
                        if (customItem.name && customItem.price > 0) {
                          onAddProduct(customItem, "autres");
                          setCustomItem({ name: "", price: 0 });
                        }
                      }}
                      disabled={!customItem.name || customItem.price <= 0}
                      className="bg-amber-500 hover:bg-amber-600 h-8 px-2"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT SIDE: Current Items */}
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                Articles ({editingItems.length})
              </h3>

              <div className="space-y-1.5 max-h-[150px] sm:max-h-[200px] overflow-y-auto pr-1">
                {editingItems.map((item, index) => {
                  const deptConfig = DEPARTMENT_CONFIG[item.department] || DEPARTMENT_CONFIG.autres;
                  return (
                    <div
                      key={index}
                      className={`flex items-center justify-between gap-1 rounded-lg p-1.5 border ${deptConfig.borderColor} ${deptConfig.bgColor}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-xs truncate">{item.name}</p>
                        <p className={`text-xs ${deptConfig.color}`}>{formatPrice(item.price)} F</p>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Button size="sm" variant="ghost" onClick={() => onUpdateQty(index, -1)} className="text-white h-6 w-6 p-0">
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="text-white font-bold w-5 text-center text-xs">{item.quantity}</span>
                        <Button size="sm" variant="ghost" onClick={() => onUpdateQty(index, 1)} className="text-white h-6 w-6 p-0">
                          <Plus className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onRemoveItem(index)}
                          className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {editingItems.length === 0 && <p className="text-slate-500 text-xs text-center py-4">Aucun article</p>}
              </div>

              {/* Total */}
              <div className="border-t border-slate-700 pt-2">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Total:</span>
                  <span className="text-amber-500 font-bold text-lg">
                    {formatPrice(editingItems.reduce((sum, item) => sum + item.price * item.quantity, 0))} F
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed buttons at bottom */}
        <div className="flex-shrink-0 flex gap-2 pt-3 border-t border-slate-700 bg-slate-800">
          <Button
            onClick={onSave}
            className="flex-1 bg-green-600 hover:bg-green-700 h-12 text-base"
            disabled={editingItems.length === 0}
            data-testid="editing-invoice-save-btn"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            OK - Enregistrer
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            className="border-slate-600 text-slate-400 h-12 px-6"
            data-testid="editing-invoice-cancel-btn"
          >
            Annuler
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditingInvoiceModal;
